import { Check, ChevronDown, Globe, Play, Search, Star, Users, Volume2 } from 'lucide-react-native';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type ViewStyle,
} from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useElevenLabsVoices, useRecommendedVoices } from '../hooks/useVoice';
import type { ElevenLabsVoice, SharedVoice } from '../types';

type Tab = 'recommended' | 'all';

interface VoiceSelectorProps {
  value: string;
  onChange: (voiceId: string) => void;
  onPreview?: (voice: ElevenLabsVoice) => void;
  disabled?: boolean;
}

interface NormalizedVoice {
  voice_id: string;
  name: string;
  category: string;
  preview_url: string;
  accent: string;
  gender: string;
  language: string;
  description: string;
  popularity: number;
  source: 'account' | 'library';
}

function normalizeAccountVoice(v: ElevenLabsVoice): NormalizedVoice {
  return {
    voice_id: v.voice_id,
    name: v.name,
    category: v.category || '',
    preview_url: v.preview_url || '',
    accent: v.labels?.accent || '',
    gender: v.labels?.gender || '',
    language: v.labels?.language || '',
    description: v.description || '',
    popularity: 0,
    source: 'account',
  };
}

function normalizeSharedVoice(v: SharedVoice): NormalizedVoice {
  return {
    voice_id: v.voice_id,
    name: v.name,
    category: v.category || v.use_case || '',
    preview_url: v.preview_url || '',
    accent: v.accent || '',
    gender: v.gender || '',
    language: v.language || '',
    description: v.description || '',
    popularity: v.usage_character_count_1y || 0,
    source: 'library',
  };
}

function VoiceMetaTag({ label }: Readonly<{ label: string }>) {
  return (
    <Box
      backgroundColor={colors.surface.row}
      borderRadius="full"
      borderWidth={1}
      borderColor={colors.border.subtle}
      paddingX="sm"
      paddingY="xxs"
    >
      <Text variant="caption" color={colors.text.tertiary}>
        {label}
      </Text>
    </Box>
  );
}

function TabButton({
  active,
  label,
  icon,
  onPress,
}: Readonly<{
  active: boolean;
  label: string;
  icon: ReactNode;
  onPress: () => void;
}>) {
  return (
    <Pressable onPress={onPress} style={{ flex: 1 }}>
      <Box
        backgroundColor={active ? colors.primary.subtle : 'transparent'}
        borderRadius="lg"
        paddingY="sm"
        alignItems="center"
        style={active ? styles.activeTab : undefined}
      >
        <HStack spacing="xs" align="center">
          {icon}
          <Text
            variant="caption"
            color={active ? colors.primary.main : colors.text.tertiary}
            style={{ fontWeight: active ? '600' : '400' }}
          >
            {label}
          </Text>
        </HStack>
      </Box>
    </Pressable>
  );
}

function GenderFilter({
  selected,
  onSelect,
}: Readonly<{
  selected: string | null;
  onSelect: (g: string | null) => void;
}>) {
  const options = ['male', 'female'];
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ paddingHorizontal: tokens.spacing.md, gap: tokens.spacing.sm }}
    >
      {options.map((g) => {
        const active = selected === g;
        return (
          <Pressable key={g} onPress={() => onSelect(active ? null : g)}>
            <Box
              backgroundColor={active ? colors.primary.subtle : colors.background.input}
              borderRadius="full"
              borderWidth={1}
              borderColor={active ? colors.primary.main : colors.border.input}
              paddingX="md"
              paddingY="xs"
            >
              <Text
                variant="caption"
                color={active ? colors.primary.main : colors.text.secondary}
                style={{ fontWeight: active ? '600' : '400', textTransform: 'capitalize' }}
              >
                {g}
              </Text>
            </Box>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function VoiceSelector({
  value,
  onChange,
  onPreview,
  disabled,
}: Readonly<VoiceSelectorProps>) {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id || '';

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('recommended');
  const [genderFilter, setGenderFilter] = useState<string | null>(null);

  const { data: voices, isLoading: isLoadingVoices } = useElevenLabsVoices();
  const { data: recommended, isLoading: isLoadingRecommended } = useRecommendedVoices(orgId);

  const selectedVoice = voices?.find((voice) => voice.voice_id === value);

  const recommendedVoices = useMemo(() => {
    if (!recommended) return [];
    const accountNormalized = recommended.accountVoices.map(normalizeAccountVoice);
    const libraryNormalized = recommended.libraryVoices.map(normalizeSharedVoice);
    const seen = new Set(accountNormalized.map((v) => v.voice_id));
    const deduped = [
      ...accountNormalized,
      ...libraryNormalized.filter((v) => !seen.has(v.voice_id)),
    ];
    return deduped;
  }, [recommended]);

  const allVoices = useMemo(() => {
    return (voices || []).map(normalizeAccountVoice);
  }, [voices]);

  const currentList = activeTab === 'recommended' ? recommendedVoices : allVoices;
  const isLoading = activeTab === 'recommended' ? isLoadingRecommended : isLoadingVoices;

  const filteredVoices = useMemo(() => {
    let list = currentList;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.category.toLowerCase().includes(q) ||
          v.language.toLowerCase().includes(q) ||
          v.accent.toLowerCase().includes(q),
      );
    }
    if (genderFilter) {
      list = list.filter((v) => v.gender.toLowerCase() === genderFilter);
    }
    return list;
  }, [currentList, searchQuery, genderFilter]);

  const handleSelect = useCallback(
    (voice: NormalizedVoice) => {
      onChange(voice.voice_id);
      setIsOpen(false);
    },
    [onChange],
  );

  const handlePreview = useCallback(
    (voice: NormalizedVoice) => {
      if (onPreview) {
        onPreview({
          voice_id: voice.voice_id,
          name: voice.name,
          category: voice.category,
          preview_url: voice.preview_url,
          labels: { accent: voice.accent, gender: voice.gender, language: voice.language },
        });
      }
    },
    [onPreview],
  );

  const renderVoiceItem = useCallback(
    ({ item }: Readonly<{ item: NormalizedVoice }>) => {
      const isSelected = item.voice_id === value;

      const rowHoverStyle = Platform.select({
        web: {
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          cursor: 'pointer',
        } as ViewStyle,
        default: {},
      });

      return (
        <Pressable onPress={() => handleSelect(item)}>
          <Box
            backgroundColor={isSelected ? colors.primary.subtle : 'transparent'}
            borderBottomWidth={1}
            borderBottomColor={colors.border.subtle}
            paddingY="md"
            paddingX="md"
            style={rowHoverStyle}
          >
            <HStack justify="between" align="center">
              <HStack spacing="md" align="center" style={{ flex: 1 }}>
                <Box style={styles.voiceIconWrap}>
                  {item.source === 'library' ? (
                    <Globe size={16} color={colors.accent.blue[400]} />
                  ) : (
                    <Volume2 size={16} color={colors.primary.main} />
                  )}
                </Box>

                <VStack spacing="xxs" style={{ flex: 1 }}>
                  <HStack spacing="xs" align="center">
                    <Text
                      variant="body"
                      color={colors.text.primary}
                      style={{ fontWeight: isSelected ? '600' : '500' }}
                    >
                      {item.name}
                    </Text>
                    {item.source === 'library' && item.popularity > 1_000_000 && (
                      <Star size={12} color={colors.accent.amber[400]} />
                    )}
                  </HStack>

                  <HStack spacing="xs" style={styles.metaRow}>
                    {Boolean(item.category) && <VoiceMetaTag label={item.category} />}
                    {Boolean(item.accent) && <VoiceMetaTag label={item.accent} />}
                    {Boolean(item.gender) && <VoiceMetaTag label={item.gender} />}
                    {Boolean(item.language) && <VoiceMetaTag label={item.language} />}
                  </HStack>
                </VStack>
              </HStack>

              <HStack spacing="sm" align="center">
                {Boolean(item.preview_url) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={(event) => {
                      event?.stopPropagation?.();
                      handlePreview(item);
                    }}
                    style={styles.previewButton}
                  >
                    <Play size={14} color={colors.text.secondary} />
                  </Button>
                )}
                {isSelected && <Check size={18} color={colors.primary.main} />}
              </HStack>
            </HStack>
          </Box>
        </Pressable>
      );
    },
    [handleSelect, handlePreview, value],
  );

  const languageLabel = recommended?.language ? recommended.language.toUpperCase() : '';

  return (
    <>
      <Pressable onPress={() => !disabled && setIsOpen(true)} disabled={disabled}>
        <Box
          backgroundColor={colors.background.input}
          borderRadius="lg"
          borderWidth={1}
          borderColor={colors.border.input}
          padding="md"
          style={{ opacity: disabled ? 0.5 : 1 }}
        >
          <HStack justify="between" align="center">
            <HStack spacing="md" align="center" style={{ flex: 1 }}>
              <Box style={styles.voiceIconWrap}>
                <Volume2 size={16} color={colors.text.tertiary} />
              </Box>
              {selectedVoice ? (
                <VStack spacing="xxs" style={{ flex: 1 }}>
                  <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
                    {selectedVoice.name}
                  </Text>
                  <Text variant="caption" color={colors.text.tertiary}>
                    {selectedVoice.category}
                  </Text>
                </VStack>
              ) : (
                <Text variant="body" color={colors.text.tertiary}>
                  {isLoadingVoices ? t('common.states.loading') : t('voice.form.voice.placeholder')}
                </Text>
              )}
            </HStack>
            <ChevronDown size={18} color={colors.text.tertiary} />
          </HStack>
        </Box>
      </Pressable>

      <Modal
        visible={isOpen}
        animationType="slide"
        presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : 'fullScreen'}
        onRequestClose={() => setIsOpen(false)}
      >
        <Box backgroundColor={colors.background.primary} style={{ flex: 1 }}>
          <Box style={styles.modalHeader}>
            <HStack justify="between" align="center">
              <Text variant="h3" color={colors.text.primary}>
                {t('voice.form.voice.label')}
              </Text>
              <Button variant="ghost" size="sm" onPress={() => setIsOpen(false)}>
                <Text variant="body" color={colors.primary.main}>
                  {t('common.actions.done')}
                </Text>
              </Button>
            </HStack>

            <Box marginTop="md">
              <HStack
                spacing="xs"
                style={{
                  backgroundColor: colors.background.input,
                  borderRadius: tokens.radius.lg,
                  padding: 4,
                }}
              >
                <TabButton
                  active={activeTab === 'recommended'}
                  label={languageLabel ? `Recommended (${languageLabel})` : 'Recommended'}
                  icon={
                    <Star
                      size={14}
                      color={
                        activeTab === 'recommended' ? colors.primary.main : colors.text.tertiary
                      }
                    />
                  }
                  onPress={() => setActiveTab('recommended')}
                />
                <TabButton
                  active={activeTab === 'all'}
                  label="All Voices"
                  icon={
                    <Users
                      size={14}
                      color={activeTab === 'all' ? colors.primary.main : colors.text.tertiary}
                    />
                  }
                  onPress={() => setActiveTab('all')}
                />
              </HStack>
            </Box>

            <Box marginTop="md" style={styles.searchContainer}>
              <HStack spacing="sm" align="center">
                <Search size={18} color={colors.text.tertiary} />
                <FormInput
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder={t('components.primitives.searchPlaceholder')}
                  style={styles.searchInput}
                />
              </HStack>
            </Box>

            <Box marginTop="sm">
              <GenderFilter selected={genderFilter} onSelect={setGenderFilter} />
            </Box>
          </Box>

          <FlatList
            data={filteredVoices}
            renderItem={renderVoiceItem}
            keyExtractor={(item) => `${item.source}-${item.voice_id}`}
            contentContainerStyle={{ paddingBottom: tokens.spacing.xxlPlus }}
            ListEmptyComponent={
              <Box padding="xl" alignItems="center">
                {isLoading ? (
                  <ActivityIndicator color={colors.primary.main} />
                ) : (
                  <Text variant="body" color={colors.text.tertiary}>
                    {t('voice.template.empty.title')}
                  </Text>
                )}
              </Box>
            }
          />
        </Box>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  voiceIconWrap: {
    width: 34,
    height: 34,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.iconWell,
  },
  metaRow: {
    flexWrap: 'wrap',
  },
  previewButton: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.md,
  },
  modalHeader: {
    backgroundColor: colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
    padding: tokens.spacing.lg,
  },
  searchContainer: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    paddingHorizontal: tokens.spacing.md,
  },
  searchInput: {
    flex: 1,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 44,
  },
  activeTab: {
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
  },
});
